import { MenuItem, Stack, TextField } from "@mui/material";
import RemoveIconButton from "./RemoveIconButton";
import {
  literalValue,
  OPERATORS,
  VARIABLES,
  variableSpec,
  type LeafCondition,
  type Operator,
  type Variable,
  type WizardParameter,
} from "./clearanceRulesConfig";
import ValueField from "./ValueField";
import t from "../translations";

const OPERATOR_LABELS: Record<Operator, string> = {
  less_than: t.OperatorLessThan,
  less_than_or_equal_to: t.OperatorLessThanOrEqualTo,
  greater_than: t.OperatorGreaterThan,
  greater_than_or_equal_to: t.OperatorGreaterThanOrEqualTo,
  equal_to: t.OperatorEqualTo,
};

type Props = {
  condition: LeafCondition;
  parameters: WizardParameter[];
  onChange: (condition: LeafCondition) => void;
  onRemove: () => void;
  disableRemove?: boolean;
};

export default function LeafConditionRow({
  condition,
  parameters,
  onChange,
  onRemove,
  disableRemove,
}: Props) {
  const spec = variableSpec(condition.variable);

  return (
    <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
      <TextField
        select
        size="small"
        label={t.VariableLabel}
        value={condition.variable}
        onChange={(event) => {
          const variable = event.target.value as Variable;
          const nextSpec = variableSpec(variable);
          // An enum-valued variable (e.g. classification) is a category, not
          // an ordinal distance - reset the operator/value so a leftover
          // distance comparison (or vice versa) can't linger unnoticed.
          onChange({
            ...condition,
            variable,
            operator: nextSpec.valueKind === "enum" ? "equal_to" : condition.operator,
            value: literalValue(),
          });
        }}
        sx={{ width: 240 }}
      >
        {VARIABLES.map(({ name }) => (
          <MenuItem key={name} value={name}>
            {name}
          </MenuItem>
        ))}
      </TextField>
      <TextField
        select
        size="small"
        label={t.OperatorLabel}
        value={condition.operator}
        onChange={(event) =>
          onChange({ ...condition, operator: event.target.value as Operator })
        }
        sx={{ width: 200 }}
      >
        {OPERATORS.map((operator) => (
          <MenuItem key={operator} value={operator}>
            {OPERATOR_LABELS[operator]}
          </MenuItem>
        ))}
      </TextField>
      <ValueField
        label={t.ValueLabel}
        value={condition.value}
        parameters={parameters}
        onChange={(value) => onChange({ ...condition, value })}
        literalOptions={
          spec.valueKind === "enum"
            ? spec.options.map(({ name, value }) => ({ value, label: `${name} (${value})` }))
            : undefined
        }
      />
      <RemoveIconButton
        aria-label={t.RemoveConditionButton}
        onClick={onRemove}
        disabled={disableRemove}
      />
    </Stack>
  );
}
